var __awaiter = this && this.__awaiter || function (thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }

  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }

    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }

    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }

    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
};

import { v4 as uuidv4 } from 'uuid';
import logger from './util/logger';
import Connection from './services/Connection';
import Setup from './services/Setup';
import { deRegister, register, trigger, deRegisterAll } from './services/Handler';
import BroadcastHandler from './services/BroadcastHandler';
import { ADD, REMOVE, SwEvent, BladeMethod } from './util/constants';
import { NOTIFICATION_TYPE } from './webrtc/constants';
import { Subscription, Connect, Reauthenticate, Ping } from './messages/Blade';
import { isFunction, randomInt } from './util/helpers';
import { sessionStorage } from './util/storage/';
const KEEPALIVE_INTERVAL = 10 * 1000;
export default class BaseSession {
  constructor(options) {
    this.options = options;
    this.uuid = uuidv4();
    this.sessionid = '';
    this.subscriptions = {};
    this.expiresAt = 0;
    this.signature = null;
    this.relayProtocol = null;
    this.contexts = [];
    this.timeoutErrorCode = -32000;
    this.connection = null;
    this._jwtAuth = false;
    this._doKeepAlive = false;
    this._autoReconnect = true;
    this._idle = false;
    this._executeQueue = [];

    if (!this.validateOptions()) {
      throw new Error('Invalid init options');
    }

    this._onSocketOpen = this._onSocketOpen.bind(this);
    this._onSocketCloseOrError = this._onSocketCloseOrError.bind(this);
    this._onSocketMessage = this._onSocketMessage.bind(this);
    this._handleLoginError = this._handleLoginError.bind(this);
    this._checkTokenExpiration = this._checkTokenExpiration.bind(this);

    this._attachListeners();

    this.connection = new Connection(this);
  }

  get __logger() {
    return logger;
  }

  get connected() {
    return this.connection && this.connection.connected;
  }

  get expired() {
    return this.expiresAt && this.expiresAt <= Date.now() / 1000;
  }

  get reconnectDelay() {
    return randomInt(6, 2) * 1000;
  }

  execute(msg) {
    if (this._idle) {
      return new Promise(resolve => this._executeQueue.push({
        resolve,
        msg
      }));
    }

    if (!this.connected) {
      return new Promise(resolve => {
        this._executeQueue.push({
          resolve,
          msg
        });

        this.connect();
      });
    }

    return this.connection.send(msg).catch(error => {
      if (error.code && error.code === this.timeoutErrorCode) {
        this._closeConnection();
      }

      throw error;
    });
  }

  executeRaw(text) {
    if (this._idle) {
      this._executeQueue.push({
        msg: text
      });

      return;
    }

    this.connection.sendRawText(text);
  }

  validateOptions() {
    const {
      project = false,
      token = false
    } = this.options;
    return Boolean(project && token);
  }

  broadcast(params) {}

  subscribe(_ref) {
    let {
      protocol,
      channels,
      handler
    } = _ref;
    return __awaiter(this, void 0, void 0, function* () {
      const bs = new Subscription({
        command: ADD,
        protocol,
        channels
      });
      const result = yield this.execute(bs);
      const {
        failed_channels = [],
        subscribe_channels = []
      } = result;

      if (failed_channels.length) {
        failed_channels.forEach(channel => this._removeSubscription(protocol, channel));
      }

      subscribe_channels.forEach(channel => this._addSubscription(protocol, handler, channel));
      return result;
    });
  }

  unsubscribe(_ref2) {
    let {
      protocol,
      channels,
      handler
    } = _ref2;
    return __awaiter(this, void 0, void 0, function* () {
      const bs = new Subscription({
        command: REMOVE,
        protocol,
        channels
      });
      return this.execute(bs);
    });
  }

  disconnect() {
    return __awaiter(this, void 0, void 0, function* () {
      clearTimeout(this._reconnectTimeout);
      this.subscriptions = {};
      this._autoReconnect = false;
      this.relayProtocol = null;

      this._closeConnection();

      yield sessionStorage.removeItem(this.signature);
      this._executeQueue = [];

      this._detachListeners();
    });
  }

  on(eventName, callback) {
    register(eventName, callback, this.uuid);
    return this;
  }

  off(eventName, callback) {
    deRegister(eventName, callback, this.uuid);
    return this;
  }

  refreshToken(token) {
    return __awaiter(this, void 0, void 0, function* () {
      this.options.token = token;

      try {
        if (this.expired) {
          yield this.connect();
        } else {
          const br = new Reauthenticate(this.options.project, token, this.sessionid);
          const response = yield this.execute(br);
          const {
            authorization: {
              expires_at = null
            } = {}
          } = response;
          this.expiresAt = +expires_at || 0;
        }
      } catch (error) {
        logger.error('refreshToken error:', error);
        trigger(SwEvent.Error, error, this.uuid, false);
      }
    });
  }

  connect() {
    return __awaiter(this, void 0, void 0, function* () {
      if (!this.connection) {
        this.connection = new Connection(this);
      }

      this._attachListeners();

      if (!this.connection.isAlive) {
        this.connection.connect();
      }
    });
  }

  _handleLoginError(error) {
    trigger(SwEvent.Error, error, this.uuid);
  }

  _onSocketOpen() {
    return __awaiter(this, void 0, void 0, function* () {
      this._idle = false;
      const tokenKey = this._jwtAuth ? 'jwt_token' : 'token';
      const {
        project,
        token
      } = this.options;
      const bc = new Connect({
        project,
        [tokenKey]: token
      }, this.sessionid);
      const response = yield this.execute(bc).catch(this._handleLoginError);

      if (response) {
        this._autoReconnect = true;
        const {
          sessionid,
          nodeid,
          master_nodeid,
          authorization: {
            expires_at = null,
            signature = null
          } = {}
        } = response;
        this.expiresAt = +expires_at || 0;
        this.signature = signature;
        this.relayProtocol = yield Setup(this);

        this._checkTokenExpiration();

        this.sessionid = sessionid;
        this.nodeid = nodeid;
        this.master_nodeid = master_nodeid;

        this._emptyExecuteQueues();

        this._pong = null;

        this._keepAlive();

        trigger(SwEvent.Ready, this, this.uuid);
        logger.info('Session Ready!');
      }
    });
  }

  _onSocketCloseOrError(event) {
    logger.error("Socket ".concat(event.type, " ").concat(event.message));

    if (this.relayProtocol) {
      deRegisterAll(this.relayProtocol);
    }

    for (const sub in this.subscriptions) {
      deRegisterAll(sub);
    }

    this.subscriptions = {};
    this.contexts = [];

    if (this.expired) {
      this._idle = true;
      this._autoReconnect = false;
      this.expiresAt = 0;
    }

    if (this._autoReconnect) {
      this._reconnectTimeout = setTimeout(() => this.connect(), this.reconnectDelay);
    }
  }

  _onSocketMessage(response) {
    const {
      method,
      params
    } = response;

    switch (method) {
      case BladeMethod.Broadcast:
        BroadcastHandler(this, params);
        break;

      case BladeMethod.Disconnect:
        this._idle = true;
        break;
    }
  }

  _removeSubscription(protocol, channel) {
    if (!this._existsSubscription(protocol, channel)) {
      return;
    }

    if (channel) {
      delete this.subscriptions[protocol][channel];
      deRegister(protocol, null, channel);
    } else {
      delete this.subscriptions[protocol];
      deRegisterAll(protocol);
    }
  }

  _addSubscription(protocol) {
    let handler = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    let channel = arguments.length > 2 ? arguments[2] : undefined;

    if (this._existsSubscription(protocol, channel)) {
      return;
    }

    if (!this._existsSubscription(protocol)) {
      this.subscriptions[protocol] = {};
    }

    this.subscriptions[protocol][channel] = {};

    if (isFunction(handler)) {
      register(protocol, handler, channel);
    }
  }

  _existsSubscription(protocol, channel) {
    if (this.subscriptions.hasOwnProperty(protocol)) {
      if (!channel || channel && this.subscriptions[protocol].hasOwnProperty(channel)) {
        return true;
      }
    }

    return false;
  }

  _attachListeners() {
    this._detachListeners();

    this.on(SwEvent.SocketOpen, this._onSocketOpen);
    this.on(SwEvent.SocketClose, this._onSocketCloseOrError);
    this.on(SwEvent.SocketError, this._onSocketCloseOrError);
    this.on(SwEvent.SocketMessage, this._onSocketMessage);
  }

  _detachListeners() {
    this.off(SwEvent.SocketOpen, this._onSocketOpen);
    this.off(SwEvent.SocketClose, this._onSocketCloseOrError);
    this.off(SwEvent.SocketError, this._onSocketCloseOrError);
    this.off(SwEvent.SocketMessage, this._onSocketMessage);
  }

  _emptyExecuteQueues() {
    this._executeQueue.forEach(_ref3 => {
      let {
        resolve,
        msg
      } = _ref3;

      if (typeof msg === 'string') {
        this.executeRaw(msg);
      } else {
        resolve(this.execute(msg));
      }
    });
  }

  _closeConnection() {
    this._idle = true;
    clearTimeout(this._keepAliveTimeout);

    if (this.connection) {
      this.connection.close();
    }
  }

  _checkTokenExpiration() {
    if (!this.expiresAt) {
      return;
    }

    const diff = this.expiresAt - Date.now() / 1000;

    if (diff <= 60) {
      logger.warn('Your JWT is going to expire. You should refresh it to keep the session live.');
      trigger(SwEvent.Notification, {
        type: NOTIFICATION_TYPE.refreshToken,
        session: this
      }, this.uuid, false);
    }

    if (!this.expired) {
      setTimeout(this._checkTokenExpiration, 30 * 1000);
    }
  }

  _keepAlive() {
    if (this._doKeepAlive !== true) {
      return;
    }

    if (this._pong === false) {
      return this._closeConnection();
    }

    this._pong = false;
    this.execute(new Ping()).then(() => this._pong = true).catch(() => this._pong = false);
    this._keepAliveTimeout = setTimeout(() => this._keepAlive(), KEEPALIVE_INTERVAL);
  }

  static on(eventName, callback) {
    register(eventName, callback);
  }

  static off(eventName) {
    deRegister(eventName);
  }

  static uuid() {
    return uuidv4();
  }

}